import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppVulnerability } from "@/types/appVulnerability";
import { ExternalLink, AlertTriangle, Calendar, Clock, Shield, Package, Monitor } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { SeverityBadge } from "../shared/SeverityBadge";
import { CVSSScoreDisplay } from "../shared/CVSSScoreDisplay";

interface AppVulnerabilityDetailPanelProps {
  vulnerability: AppVulnerability | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AppVulnerabilityDetailPanel({ vulnerability, open, onOpenChange }: AppVulnerabilityDetailPanelProps) {
  if (!vulnerability) return null;

  const daysAgo = Math.floor(
    (new Date().getTime() - new Date(vulnerability.published_date).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <SheetTitle className="text-2xl font-mono">{vulnerability.cve_id}</SheetTitle>
              <a
                href={`https://nvd.nist.gov/vuln/detail/${vulnerability.cve_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:underline flex items-center gap-1 mt-1"
              >
                View on NVD <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </SheetHeader>

        {/* Description */}
        <div className="mt-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {vulnerability.description || "No description available."}
          </p>
        </div>

        {/* Quick Metrics */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              Severity
            </div>
            <SeverityBadge severity={vulnerability.severity} className="text-base" />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              CVSS Score
            </div>
            <CVSSScoreDisplay score={vulnerability.cvss_score} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Known Exploit
            </div>
            <div className="text-base font-semibold">
              {vulnerability.actively_exploited ? (
                <span className="text-[hsl(30_80%_55%)]">Yes - Active</span>
              ) : (
                <span className="text-muted-foreground">No</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Age
            </div>
            <div className="text-base font-semibold">{daysAgo} days</div>
          </div>
        </div>

        {/* Affected Applications */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Affected Applications</span>
          </div>
          <div className="space-y-3">
            {vulnerability.affected_apps.map((app, idx) => {
              const hasIcon = app.icon_url && (app.icon_url.startsWith('data:') || app.icon_url.startsWith('http'));
              return (
                <div key={idx} className="flex items-center gap-3 p-3 bg-background rounded-lg">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted">
                    {hasIcon ? (
                      <img 
                        src={app.icon_url!} 
                        alt={app.app_name}
                        className="w-8 h-8 rounded object-cover"
                      />
                    ) : (
                      <Package className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{app.app_name}</div>
                    <div className="text-xs text-muted-foreground">Version {app.app_version}</div>
                  </div>
                </div>
              );
            })}
            <div className="flex justify-between text-sm pt-2 border-t border-border">
              <span className="text-muted-foreground">Total Affected Devices</span>
              <span className="font-medium flex items-center gap-1">
                <Monitor className="h-3 w-3" />
                {vulnerability.affected_devices_count}
              </span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Timeline</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Published</span>
              <span className="font-medium">{format(new Date(vulnerability.published_date), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last Modified</span>
              <span className="font-medium">{format(new Date(vulnerability.last_modified_date), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last Detected</span>
              <span className="font-medium">{formatDistanceToNow(new Date(vulnerability.updated_at), { addSuffix: true })}</span>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="remediation" className="mt-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="remediation">Remediation</TabsTrigger>
            <TabsTrigger value="insights">Risk Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="remediation" className="space-y-4 mt-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold mb-2">Recommended Actions</h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Update {vulnerability.affected_apps.length === 1 ? vulnerability.affected_apps[0].app_name : `all ${vulnerability.affected_apps.length} affected applications`} to the latest version</li>
                  <li>Deploy update to {vulnerability.affected_devices_count} affected devices</li>
                  <li>Verify successful installation on all devices</li>
                  <li>Re-scan inventory to confirm vulnerability remediation</li>
                </ol>
              </div>

              {vulnerability.actively_exploited && (
                <div className="p-4 bg-[hsl(30_80%_55%)]/10 border border-[hsl(30_80%_55%)]/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-[hsl(30_80%_55%)] mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-[hsl(30_80%_55%)]">Urgent: Actively Exploited</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        This vulnerability is being actively exploited. Prioritize immediate patching across all affected devices.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2">Mitigation Steps</h4>
                <p className="text-sm text-muted-foreground">
                  Until updates can be deployed, consider limiting application usage or restricting network access for affected devices.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-4 mt-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold mb-2">Impact Assessment</h4>
                <p className="text-sm text-muted-foreground">
                  This vulnerability affects {vulnerability.affected_devices_count} devices in your organization running {vulnerability.affected_apps.length === 1 
                    ? `${vulnerability.affected_apps[0].app_name} version ${vulnerability.affected_apps[0].app_version}`
                    : `${vulnerability.affected_apps.length} different applications`}.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Risk Level</h4>
                <p className="text-sm text-muted-foreground">
                  {vulnerability.severity === 'CRITICAL' && 
                    "This is a critical vulnerability that requires immediate attention. It poses a significant security risk to your organization."
                  }
                  {vulnerability.severity === 'HIGH' && 
                    "This is a high severity vulnerability that should be prioritized for patching within your next update cycle."
                  }
                  {vulnerability.severity === 'MEDIUM' && 
                    "This is a medium severity vulnerability that should be addressed in your regular patching schedule."
                  }
                  {vulnerability.severity === 'LOW' && 
                    "This is a low severity vulnerability. Monitor and patch during routine maintenance windows."
                  }
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Deployment Recommendation</h4>
                <p className="text-sm text-muted-foreground">
                  {vulnerability.cvss_score >= 9.0 && 
                    "Deploy patches immediately to all affected devices. Consider emergency maintenance window."
                  }
                  {vulnerability.cvss_score >= 7.0 && vulnerability.cvss_score < 9.0 &&
                    "Schedule patch deployment within 7 days. Test in staging environment first."
                  }
                  {vulnerability.cvss_score >= 4.0 && vulnerability.cvss_score < 7.0 &&
                    "Schedule patch deployment within 30 days during regular maintenance window."
                  }
                  {vulnerability.cvss_score < 4.0 &&
                    "Schedule patch deployment during next quarterly update cycle."
                  }
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
